import { X } from "@phosphor-icons/react";

/**
 * Modal de confirmação reutilizável — substitui window.confirm (que falha em WebView Capacitor).
 *
 * Props:
 * - open: bool
 * - onClose: () => void
 * - onConfirm: () => void
 * - title: string
 * - description: string | node
 * - confirmLabel: string (default: "Confirmar")
 * - cancelLabel: string (default: "Cancelar")
 * - variant: "danger" | "primary" | "success" (default: "primary")
 * - loading: bool (bloqueia botões durante ação)
 * - testid: string (base p/ data-testids)
 */
export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  variant = "primary",
  loading = false,
  testid = "confirm-dialog",
}) {
  if (!open) return null;

  const btnCls = {
    danger: "bg-red-600 hover:bg-red-700 text-white",
    primary: "bg-[#004E98] hover:bg-[#003b73] text-white",
    success: "bg-emerald-600 hover:bg-emerald-700 text-white",
  }[variant] || "bg-[#004E98] text-white";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => !loading && e.target === e.currentTarget && onClose()}
      data-testid={testid}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-heading font-black text-lg text-slate-900" data-testid={`${testid}-title`}>{title}</h3>
          {!loading && (
            <button
              type="button"
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700 shrink-0"
              aria-label="Fechar"
              data-testid={`${testid}-close`}
            >
              <X size={20} weight="bold" />
            </button>
          )}
        </div>
        {description && (
          <div className="text-sm text-slate-600 leading-relaxed mb-4" data-testid={`${testid}-desc`}>
            {description}
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded-full border-2 border-slate-200 text-slate-700 text-sm font-bold hover:bg-slate-50 disabled:opacity-40"
            data-testid={`${testid}-cancel`}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`px-4 py-2 rounded-full text-sm font-bold ${btnCls} disabled:opacity-60`}
            data-testid={`${testid}-confirm`}
          >
            {loading ? "Aguarde..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Overlay com barra de progresso para ações em lote (duplicar/excluir em massa).
 *
 * Props:
 * - open: bool
 * - title: string
 * - current: number
 * - total: number
 * - okCount / failCount: contadores
 * - testid: base
 */
export function BulkProgressOverlay({ open, title, current, total, okCount = 0, failCount = 0, testid = "bulk-progress" }) {
  if (!open) return null;
  const pct = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0;
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-4" data-testid={testid}>
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-5">
        <h3 className="font-heading font-black text-lg text-slate-900 mb-2" data-testid={`${testid}-title`}>{title}</h3>
        <div className="text-sm text-slate-600 mb-3" data-testid={`${testid}-counter`}>
          Processando <b>{current}</b> de <b>{total}</b>...
        </div>
        <div className="w-full h-3 rounded-full bg-slate-100 overflow-hidden mb-2">
          <div
            className="h-full bg-[#004E98] transition-all duration-200"
            style={{ width: `${pct}%` }}
            data-testid={`${testid}-bar`}
          />
        </div>
        <div className="flex items-center justify-between text-[11px] font-bold">
          <span className="text-slate-500" data-testid={`${testid}-pct`}>{pct}%</span>
          <span>
            <span className="text-emerald-700" data-testid={`${testid}-ok`}>{okCount} ok</span>
            {failCount > 0 && (
              <span className="text-red-600 ml-2" data-testid={`${testid}-fail`}>{failCount} falha(s)</span>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}
